<script>
import { uiState } from "@app/store/uiStore.js";
import { environment } from "@app/store/envStore.js";
import { modalState } from "@app/store/modalStore.js";
import { styleState } from "@app/store/styleStore.js";
import { externalDataState } from "@app/store/externalDataStore.js";
import { toast } from "@zerodevx/svelte-toast";
import { fade } from "svelte/transition";
import { projectState } from "@app/store/projectStore.js";
import { playerState } from "@app/store/playerStore.js";
import { markerState } from "@app/store/markerStore.js";
import { issueState } from "@app/store/issueStore.js";
import { speakerState } from "@app/store/speakerStore.js";
import { metadataState } from "@app/store/metadataStore.js";
import { eventGroupState } from "@app/store/eventGroupStore.js";
import { historyState } from "@app/store/historyStore.js";
import { authState } from "@app/store/authStore.js";
import { onMount, onDestroy } from "svelte";

import _Event from "@app/external/cc-lib/dist/classes/event.js";
import _EventGroup from "@app/external/cc-lib/dist/classes/eventGroup.js";
import _Project from "@app/external/cc-lib/dist/classes/project.js";
import _Marker from "@app/external/cc-lib/dist/classes/marker.js";
import loadMedia from "@app/external/cc-lib/dist/functions/projects/loadMedia.js";
import decode from "@app/external/cc-lib/dist/functions/decode.js";
import encode from "@app/external/cc-lib/dist/functions/encode.js";
import defaults from "@app/external/cc-lib/dist/lib/defaults.js";
import encodings from "@app/external/cc-lib/dist/dict/encodings.js";
import formats from "@app/external/cc-lib/dist/lib/formats.js";
import orderByTime from "@app/external/cc-lib/dist/functions/eventGroups/orderByTime.js";
import removeHtmlEntities from "@app/external/cc-lib/dist/functions/eventGroups/removeHtmlEntities.js";
import wrapStyledTextWithSpan from "@app/external/cc-lib/dist/functions/quill/wrapStyledTextWithSpan.js";
import frameRates from "@app/external/cc-lib/dist/dict/frameRates.js";
import supportedLanguages from "@app/external/cc-lib/dist/dict/supportedLanguages.js";
import dropFrameCv from "@app/external/cc-lib/dist/functions/special/dropFrameConvert.js";
import bufferToHex from "@app/external/cc-lib/dist/functions/utility/bufferToHex.js";
import fixOverlap from "@app/external/cc-lib/dist/functions/eventGroups/fixOverlap.js";
import checkForOverlaps from "@app/external/cc-lib/dist/functions/eventGroups/checkForOverlaps.js";
import automaticOs from "@app/external/cc-lib/dist/functions/special/automaticOffset.js";
import getAutomaticOffsetAmount from "@app/external/cc-lib/dist/functions/special/getAutomaticOffsetAmount.js";
import stripTags from "@app/external/cc-lib/dist/functions/quill/stripTags.js";
import encodeHtml from "@app/external/cc-lib/dist/functions/eventGroups/encodeHtml.js";
import replaceTrialText from "@app/external/cc-lib/dist/functions/eventGroups/replaceTrialText.js";
import tcLib from "@app/external/cc-lib/dist/lib/timecode.js";
import insertEvent from "@app/external/cc-lib/dist/functions/eventGroups/insertEvent.js";
import { WindowMessenger, connect } from "penpal";
import { ccprjFunc } from "@app/lib/ccprj.js";
import Swal from "sweetalert2";
import { saveAs } from "file-saver";

const hexColors = [
    "#ff66cc", "#00cc00", "#0066ff", "#6600ff", "#ff0066",
    "#6666ff", "#00cc99", "#99ccff", "#ff9966", "#ff33cc",
    "#009900", "#ff9933", "#009999", "#333399", "#999966",
    "#99ccff", "#990033", "#996600", "#ffcc99", "#6699ff"
];

resetProjectState();

let progress = 0,
    statusMsg = "Initializing...",
    modalTitle = "Importing Project";

let eventGroupDefaults =
    JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};

/* PenPal Setup */
const messenger = new WindowMessenger({
    remoteWindow: window.parent,
    // Defaults to the current origin.
    allowedOrigins: ["*"],
});

const connection = connect({
    messenger,
    // Methods the iframe window is exposing to the parent window.
    methods: {
        updateImportStatus(statusInfo) {
            progress = statusInfo.progress;
            statusMsg = statusInfo.msg;
            return true;
        },
        updateModalTitle(title) {
            modalTitle = title;
            return true;
        },
        completeImport() {
            //close modal
            //alert the user using Sweet Alert Library to let them know the import was successful
            Swal.fire({
                icon: "success",
                title: "Success",
                text: "Project Import Successful.",
                confirmButtonText: "OK",
            });
            setTimeout(() => {
                $uiState.timeline = true;
                modalState.hideModal();
            }, 1000);
        },
        failImport(msg) {
            //close modal
            toast.push(`Project Import Failed. ${msg}`, {
                classes: ["toast-danger"],
            });

            /* Alert the user using Sweet Alert Library */
            Swal.fire({
                icon: "error",
                title: "Error",
                text: `Project Import Failed. ${msg}`,
                confirmButtonText: "OK",
            });

            modalState.hideModal();
        },
        alertUser(alertObj) {
            /* Alert the user using Sweet Alert Library */
            Swal.fire({
                title: alertObj.title || "Alert",
                text: alertObj.text || "This is an alert message.",
                confirmButtonText: "OK",
            });
        },
        setProjectId(id) {
            $projectState.id = id;
        },
        setProjectName(name) {
            $projectState.name = name;
        },
        setProjectDescription(description) {
            $projectState.description = description;
        },
        setProjectFrameRate(frameRate) {
            $projectState.frameRate = frameRate;
        },
        setProjectDropFrame(dropFrame) {
            $projectState.dropFrame = dropFrame;
        },
        setProjectIncode(incode) {
            $projectState.incode = tcLib.tcToSec(
                incode,
                $projectState.frameRate,
                $projectState.dropFrame,
            );
        },
        setProjectState(state) {
            $projectState = state;
        },
        setMediaPeaksPath(path) {
            $projectState.media.peaksPath = path;
            $projectState.media.useFallback = false;
        },
        setMediaPeaksData(data) {
            $projectState.media.peaksData = data;
            $projectState.media.useFallback = false;
        },
        useFallbackTimeline() {
            $projectState.media.useFallback = true;
        },
        useDefaultTimeline() {
            $projectState.media.useFallback = false;
        },
        setMarkers(markers) {
            console.log(markers);
            $markerState = markers;
        },
        createMarkerList(markerList) {
            let newMarkerList = {
                id: $markerState.lists.length,
                name: markerList.name || "Unknown",
                color: markerList.color || hexColors[Math.floor(Math.random() * 20)],
                markers: [],
            };

            $markerState.lists = [...$markerState.lists, newMarkerList];
        },
        insertMarker(marker, markerList = 0) {
            let newMarker = new _Marker(marker);
            
            if (!$markerState.lists[markerList]) {
                return;
            }

            $markerState.lists[markerList].markers = [
                ...$markerState.lists[markerList].markers,
                newMarker,
            ];
        },
        setPoster(thumbnailUrl) {
            $projectState.media.thumbnail = thumbnailUrl;
            player.poster = thumbnailUrl;
        },
        setTranscript(transcript, profile = "deepgram") {
            $externalDataState.transcript = transcript;
            $externalDataState.profile = profile || "deepgram";
        },
        async loadTranscript() {
            try {
                // Create a new event group from the transcript
                let playerWidth =
                    document.getElementById("PlayerWrapper")?.clientWidth ||
                    720;
                let playerHeight =
                    document.getElementById("PlayerWrapper")?.clientHeight ||
                    480;

                let maxChars = eventGroupDefaults.maxChars || 32;
                let maxLines = eventGroupDefaults.maxLines || 2;

                // Determine the source profile
                let srcProfile = $externalDataState.profile;

                let options = new defaults.options({
                    profile:
                        formats.profileMapping[srcProfile]?.name || srcProfile,
                    formatOptions: [
                        {
                            name: "Import Type",
                            type: "list",
                            values: ["subtitle", "transcription"],
                            selected: "subtitle",
                        },
                        {
                            name: "Max Characters",
                            type: "number-input",
                            values: "",
                            selected: maxChars,
                        },
                        {
                            name: "Max Lines",
                            type: "number-input",
                            values: "",
                            selected: maxLines,
                        },
                    ],
                    frameRate: $projectState.frameRate,
                    dropFrame: $projectState.dropFrame,
                    window: {
                        width: playerWidth,
                        height: playerHeight,
                        xOffset: ($styleState.xPadding / 100) * playerWidth,
                        yOffset: ($styleState.yPadding / 100) * playerHeight,
                    },
                });

                let transcriptEventGroup = await decode(
                    JSON.stringify($externalDataState.transcript),
                    options,
                );

                transcriptEventGroup = encodeHtml(transcriptEventGroup);

                transcriptEventGroup.type = "subtitle";
                transcriptEventGroup.name = `Transcript`;
                transcriptEventGroup.maxCps = eventGroupDefaults.maxCps || 9999;
                transcriptEventGroup.maxWpm = eventGroupDefaults.maxWpm || 9999;
                transcriptEventGroup.maxChars =
                    eventGroupDefaults.maxChars || 9999;
                transcriptEventGroup.maxLines =
                    eventGroupDefaults.maxLines || 9999;
                transcriptEventGroup.overlap = eventGroupDefaults.overlap;
                transcriptEventGroup.illegalChars =
                    eventGroupDefaults.illegalChars;

                // Add speakers to the speaker state
                if (
                    transcriptEventGroup.speakers &&
                    transcriptEventGroup.speakers.length > 0
                ) {
                    $speakerState = [
                        ...$speakerState,
                        ...transcriptEventGroup.speakers,
                    ];
                }

                // Add the event group to the state
                $eventGroupState = [...$eventGroupState, transcriptEventGroup];
                $projectState.selected = $eventGroupState.length - 1;

                // Add to history
                historyState.insert({
                    name: "import transcript",
                    eventGroup: $projectState.selected,
                    snapshots: [
                        {
                            store: "eventGroupState",
                            value: JSON.stringify($eventGroupState),
                        },
                    ],
                });

                toast.push("Transcript imported successfully", {
                    classes: ["toast-success"],
                });

                return {
                    success: true,
                    message: "Transcript imported successfully",
                };
            } catch (err) {
                console.error("Error importing transcript:", err);
                toast.push(`Transcript import failed: ${err.message}`, {
                    classes: ["toast-danger"],
                });
                return { success: false, message: err.message };
            }
        },
        async importMedia(
            mediaUrl,
            thumbnailUrl,
            mediaType = "video/mp4",
            storageType = "Cloud Storage",
        ) {
            try {
                await updateStatus({
                    progress: progress,
                    msg: "Loading media...",
                });

                $projectState = loadMedia($projectState, {
                    projectName: $projectState.name || "Untitled",
                    storage: storageType,
                    mediaUrl: mediaUrl,
                    thumbnail: thumbnailUrl,
                    incode: 0,
                    frameRate: $projectState.frameRate || 24,
                    dropFrame: $projectState.dropFrame || false,
                    aspectRatio: "16/9",
                });

                console.log("Project Media State:", $projectState.media);
                if (thumbnailUrl) {
                    player.poster = thumbnailUrl;
                }

                switch (storageType) {
                    case "Vimeo":
                        player.src = $projectState.media.path;
                        break;

                    case "YouTube":
                        player.src = $projectState.media.path;
                        break;

                    case "HLS Manifest":
                        player.src = $projectState.media.path;
                        break;
                    default:
                        /* Cloud Storage */
                        let actualMediaType = mediaType;
                        if (mediaType === "video/quicktime") {
                            actualMediaType = "video/mp4";
                        } else if (mediaType === "video/x-matroska") {
                            actualMediaType = "video/mkv";
                        }

                        player.src = {
                            src: $projectState.media.path,
                            type: actualMediaType,
                        };
                        break;
                }

                return { success: true, message: "Media loaded successfully" };
            } catch (err) {
                console.error("Error loading media:", err);
                return { success: false, message: err.message };
            }
        },
        async importSubtitle(subtitleUrl, profile, decodeOptions = null, evgOptions = null) {
            try {
                // Extract file extension from URL
                const urlParts = subtitleUrl.split(".");
                const srcExt = urlParts[urlParts.length - 1].toLowerCase();
                const srcFileName = subtitleUrl.split("/").pop().split(".")[0];

                // Determine profile if not provided
                if (!profile && !srcExt) {
                    console.log(
                        "Error: No profile or file extension provided.",
                    );
                    return {
                        success: false,
                        message: "No profile or file extension provided.",
                    };
                }

                let srcProfile =
                    profile || formats.getSrcProfileByExtension(srcExt);
                let srcProfileDisplayName =
                    formats.getProfileTitleByName(srcProfile);

                if (!srcProfile) {
                    throw new Error(`Unsupported subtitle format: ${srcExt}`);
                }

                // Fetch the subtitle file
                const response = await fetch(subtitleUrl);
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch subtitle file: ${response.statusText}`,
                    );
                }

                let fileContents;
                if (formats.binaryFormats.includes(srcProfile)) {
                    const buffer = await response.arrayBuffer();
                    fileContents = bufferToHex(buffer);
                } else {
                    fileContents = await response.text();
                }

                await updateStatus({
                    progress: 30,
                    msg: "Processing subtitle file...",
                });

                // Set up event group
                let eventGroupDefaults =
                    JSON.parse(
                        localStorage.getItem("cc-event-group-defaults"),
                    ) || {};
                let eventGroupOptions = {
                    type: evgOptions?.type || "subtitle",
                    name: srcFileName,
                    linkedGroup : evgOptions?.linkedGroup ?? false,
                    language: eventGroupDefaults.language || "en-US",
                    rtl: eventGroupDefaults.rtl || false,
                    maxCps: eventGroupDefaults.maxCps || 9999,
                    maxWpm: eventGroupDefaults.maxWpm || 9999,
                    maxChars: eventGroupDefaults.maxChars || 9999,
                    maxLines: eventGroupDefaults.maxLines || 9999,
                    overlap: eventGroupDefaults.overlap,
                    illegalChars: eventGroupDefaults.illegalChars,
                };

                $eventGroupState = [
                    ...$eventGroupState,
                    new _EventGroup(eventGroupOptions),
                ];
                $projectState.selected = $eventGroupState.length - 1;

                // Get player dimensions for positioning
                let playerWidth =
                    document.getElementById("PlayerWrapper")?.clientWidth ||
                    720;
                let playerHeight =
                    document.getElementById("PlayerWrapper")?.clientHeight ||
                    480;
                let xPadding = $styleState.mode ? 18 : $styleState.xPadding;
                let yPadding = $styleState.mode ? 10 : $styleState.yPadding;

                // Set up decoding options
                let options = new defaults.options({
                    profile: srcProfile,
                    formatOptions: decodeOptions
                        ? decodeOptions
                        : formats.profileMapping[srcProfileDisplayName].options
                              ?.decode || [],
                    frameRate: $projectState.frameRate,
                    dropFrame: $projectState.dropFrame,
                    target_profile: "closedCaptionProject",
                    source_profile: srcProfile,
                    incode: tcLib.secToTc(
                        $projectState.incode,
                        $projectState.frameRate,
                        $projectState.dropFrame,
                    ),
                    window: {
                        width: playerWidth,
                        height: playerHeight,
                        xOffset: (xPadding / 100) * playerWidth,
                        yOffset: (yPadding / 100) * playerHeight,
                    },
                });

                // Decode the subtitle file
                let decodedEvGroup = await decode(fileContents, options);
                decodedEvGroup = encodeHtml(decodedEvGroup);

                // Check for automatic offset
                let offsetAmount = getAutomaticOffsetAmount(
                    decodedEvGroup,
                    $projectState.frameRate,
                    tcLib.secToTc(
                        $projectState.incode,
                        $projectState.frameRate,
                        $projectState.dropFrame,
                    ),
                );

                if (offsetAmount != 0 && $projectState.incode > 0) {
                    decodedEvGroup = automaticOs(
                        decodedEvGroup,
                        $projectState.frameRate,
                    );
                }

                // Filter out empty events for Pop-On style
                if (
                    decodedEvGroup.events.every((ev) => ev.style === "Pop-On")
                ) {
                    decodedEvGroup.events = decodedEvGroup.events.filter(
                        (event) => {
                            return (
                                stripTags(event.text) ||
                                event.notes ||
                                event.reply
                            );
                        },
                    );

                    // Fix overlaps if present
                    if (checkForOverlaps(decodedEvGroup)) {
                        decodedEvGroup = fixOverlap(decodedEvGroup);
                    }
                }

                // Import speakers from events
                decodedEvGroup.events.forEach((event) => {
                    event.speakers.forEach((speaker) => {
                        if (
                            !$speakerState.find((s) => s.name === speaker.name)
                        ) {
                            $speakerState = [...$speakerState, speaker];
                        }
                    });
                });

                // Update the event group
                $eventGroupState[$projectState.selected].events =
                    decodedEvGroup.events;

                toast.push("Subtitle file imported successfully", {
                    classes: ["toast-success"],
                });

                return {
                    success: true,
                    message: "Subtitle file imported successfully",
                };
            } catch (err) {
                console.error("Error importing subtitle file:", err);
                toast.push(`Failed to import subtitle file: ${err.message}`, {
                    classes: ["toast-danger"],
                });
                return { success: false, message: err.message };
            }
        },
        async exportSubtitle(profile, encodeOptions, saveAsFile = false) {
            try {
                let eventGroupId = getSelectedEventGroup([
                    "subtitle",
                    "translation",
                    "audio description",
                    "transcription",
                ]);

                if (!eventGroupId) {
                    throw new Error("No valid event group found for export");
                }

                let eventGroupIndex = $eventGroupState.findIndex((group) => {
                    return group.id === eventGroupId;
                });

                if (eventGroupIndex === -1) {
                    throw new Error("Event group not found");
                }

                let profileName = formats.getProfileTitleByName(profile);
                let profileExt = formats.defaultExtMap[profile];

                let fileName =
                    $eventGroupState[eventGroupIndex].name +
                    "." +
                    (profileExt || "srt");
                let copyOfEventGroup = JSON.parse(
                    JSON.stringify($eventGroupState[eventGroupIndex]),
                );

                // Parse profile information
                let frameRate = $projectState.frameRate;
                let dropFrame = $projectState.dropFrame;

                // Sort and prepare event group
                copyOfEventGroup = orderByTime(copyOfEventGroup);
                copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);

                // Apply text formatting for trial mode
                if ($authState.status === "in_trial" || $environment.system === "demo") {
                    copyOfEventGroup = replaceTrialText(copyOfEventGroup);
                }

                // Prepare styling options
                let playerWidth =
                    document.getElementById("PlayerWrapper")?.clientWidth ||
                    720;
                let playerHeight =
                    document.getElementById("PlayerWrapper")?.clientHeight ||
                    480;
                let xPadding = $styleState.mode ? 18 : $styleState.xPadding;
                let yPadding = $styleState.mode ? 10 : $styleState.yPadding;

                // Create encode options
                let options = new defaults.options({
                    profile: profile,
                    formatOptions:
                        encodeOptions ||
                        formats.profileMapping[profileName]?.options?.encode ||
                        [],
                    frameRate: frameRate,
                    dropFrame: dropFrame,
                    target_profile: profile,
                    source_profile: "closedCaptionProject",
                    incode:
                        tcLib.secToTc(
                            $projectState.incode,
                            $projectState.frameRate,
                            $projectState.dropFrame,
                        ) || "00:00:00:00",
                    window: {
                        width: playerWidth,
                        height: playerHeight,
                        xOffset: (xPadding / 100) * playerWidth,
                        yOffset: (yPadding / 100) * playerHeight,
                    },
                });

                // Apply styling to text
                copyOfEventGroup.events.forEach((event, index, events) => {
                    events[index].text = wrapStyledTextWithSpan(event.text);
                });

                // Encode to target format
                let output = await encode(copyOfEventGroup, options);

                // Determine if file should be saved or returned
                if (saveAsFile) {
                    let defaultEncoding =
                        formats.defaultEncodingMap[
                            formats.profileMapping[profileName]?.name
                        ] || "utf-8";
                    let fileBlob;

                    if (defaultEncoding === "buffer") {
                        if (profile.ext === "xlsx") {
                            fileBlob = new Blob([output], {
                                type: "application/octet-stream",
                            });
                        } else {
                            fileBlob = output;
                        }
                    } else if (defaultEncoding.toLowerCase() === "hex") {
                        let byteArray = new Uint8Array(
                            output.match(/.{2}/g).map((e) => parseInt(e, 16)),
                        );
                        fileBlob = new Blob([byteArray], {
                            type: "application/octet-stream",
                        });
                    } else if (defaultEncoding.toLowerCase() === "utf-16le") {
                        fileBlob = new Blob([output], {
                            type: "text/plain;charset=utf-16le",
                        });
                    } else {
                        fileBlob = new Blob([output], {
                            type:
                                "text/plain;charset=" +
                                (profile.encoding || defaultEncoding),
                        });
                    }

                    saveAs(fileBlob, fileName, {
                        autoBom: true,
                    });

                    return {
                        success: true,
                        message: "Subtitle exported and saved successfully",
                    };
                } else {
                    // Return the output data
                    return {
                        success: true,
                        data: output,
                        fileName: fileName,
                        message: "Subtitle exported successfully",
                    };
                }
            } catch (err) {
                console.error("Error exporting subtitle:", err);
                toast.push(`Failed to export subtitle: ${err.message}`, {
                    classes: ["toast-danger"],
                });
                return { success: false, message: err.message };
            }
        },
        loadProjectData(ccprj) {
            try {
                resetProjectState();
                const projectJson =
                    typeof ccprj === "string" ? JSON.parse(ccprj) : ccprj;

                // Update status
                updateStatus({
                    progress: 10,
                    msg: "Processing project data...",
                });

                // Set project data
                $projectState = new _Project({
                    ...projectJson,
                    ...{
                        eventGroups: [],
                        metadata: [],
                        speakers: [],
                        folderId: undefined,
                    },
                });

                updateStatus({
                    progress: 30,
                    msg: "Loading event groups...",
                });

                // Process event groups
                if (
                    projectJson.eventGroups &&
                    Array.isArray(projectJson.eventGroups)
                ) {
                    projectJson.eventGroups.forEach(
                        (eventGroup, eventGroupIndex) => {
                            $eventGroupState = [
                                ...$eventGroupState,
                                new _EventGroup({
                                    ...eventGroup,
                                    ...{
                                        events: [],
                                    },
                                }),
                            ];

                            if (
                                eventGroup.events &&
                                Array.isArray(eventGroup.events)
                            ) {
                                eventGroup.events.forEach((event) => {
                                    $eventGroupState[eventGroupIndex] =
                                        insertEvent(
                                            $eventGroupState[eventGroupIndex],
                                            event,
                                        );
                                });
                            }
                        },
                    );
                }

                updateStatus({
                    progress: 50,
                    msg: "Loading project metadata...",
                });

                // Load metadata
                if (
                    projectJson.metadata &&
                    Array.isArray(projectJson.metadata)
                ) {
                    projectJson.metadata.forEach((metadataInfo) => {
                        $metadataState = [...$metadataState, metadataInfo];
                    });
                }

                // Load speakers
                if (
                    projectJson.speakers &&
                    Array.isArray(projectJson.speakers)
                ) {
                    projectJson.speakers.forEach((speakerInfo) => {
                        $speakerState = [...$speakerState, speakerInfo];
                    });
                }

                // Load issues
                if (projectJson.issues && Array.isArray(projectJson.issues)) {
                    projectJson.issues.forEach((issue) => {
                        $issueState = [...$issueState, issue];
                    });
                }

                // Load markers
                $markerState = projectJson.markers || {
                    selected: 0,
                    lists: [
                        {
                            id: "0",
                            name: "Shot Changes",
                            color: "#E74C3C",
                            markers: [],
                        },
                    ],
                };

                // Load style info
                if (projectJson.style) {
                    for (const [key, value] of Object.entries(
                        projectJson.style,
                    )) {
                        $styleState[key] = value;
                    }
                }

                updateStatus({
                    progress: 70,
                    msg: "Setting up project...",
                });

                // Set selected event group if any exist
                if ($eventGroupState.length > 0) {
                    $projectState.selected = $eventGroupState.length - 1;
                }

                historyState.reset();
                historyState.insert({
                    name: "import project",
                    eventGroup: false,
                    snapshots: [
                        {
                            store: "eventGroupState",
                            value: JSON.stringify($eventGroupState),
                        },
                    ],
                });

                return {
                    success: true,
                    message: "Project loaded successfully",
                };
            } catch (err) {
                console.error("Error loading project data:", err);
                toast.push(`Failed to load project: ${err.message}`, {
                    classes: ["toast-danger"],
                });
                return { success: false, message: err.message };
            }
        },
        getProjectData() {
            //Return the Project json.
            return ccprjFunc.createCcprj({
                project: JSON.parse(JSON.stringify($projectState)),
                metadata: JSON.parse(JSON.stringify($metadataState)),
                speakers: JSON.parse(JSON.stringify($speakerState)),
                issues: JSON.parse(JSON.stringify($issueState)),
                markers: JSON.parse(JSON.stringify($markerState)),
                eventGroups: JSON.parse(JSON.stringify($eventGroupState)),
                style: JSON.parse(JSON.stringify($styleState)),
            });
        },
    },
});

function getSelectedEventGroup(validTypes) {
    let evGroup = $eventGroupState[$projectState.selected];
    if (evGroup) {
        if (validTypes.indexOf(evGroup.type) > -1) {
            return evGroup.id;
        } else {
            return false;
        }
    } else {
        return false;
    }
}

function resetProjectState() {
    $projectState.selected = false;
    $eventGroupState = [];
    $speakerState = [];
    $issueState = [];
    $markerState = {
        selected: 0,
        lists: [
            {
                id: "0",
                name: "Shot Changes",
                color: "#E74C3C",
                markers: [],
            },
        ],
    };
    $metadataState = [];
    playerState.updateDuration(false);
    $uiState.timeline = false;
}

async function updateStatus(statusInfo) {
    progress = statusInfo.progress;
    statusMsg = statusInfo.msg;
}

onMount(async () => {
    try {
        await updateStatus({
            progress: 10,
            msg: "Starting project import process...",
        });
    } catch (err) {
        console.error(err);
        toast.push(`Project Import Failed. ${err.message}`, {
            classes: ["toast-danger"],
        });

        /* Alert the user using Sweet Alert Library */
        await Swal.fire({
            icon: "error",
            title: "Error",
            text: `Project Import Failed. ${err.message}`,
            confirmButtonText: "OK",
        });

        modalState.hideModal();
    }
});

/* On destroy hook */
onDestroy(() => {
    console.log("Closing IFrame Import Modal");
});
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {$modalState === 'iFrameRemote' ? 'show d-block' : ''}"
    role="dialog"
    tabindex="-1"
    id="IFrameRemoteModal"
    aria-labelledby="iFrameRemoteTitle"
    aria-describedby="iFrameRemoteDesc">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 id="iFrameRemoteTitle" class="modal-title">
                    <img
                        id="systemLogoImg"
                        alt="System Logo File"
                        class="me-1"
                        src="./assets/img/logos/{$environment.system}.png" />
                    {modalTitle}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    on:click={modalState.hideModal}
                    id="closeIFrameRemote" />
            </div>
            <div class="modal-body" id="iFrameRemoteDesc">
                <p><i class="bi bi-exclamation-diamond-fill" /> {statusMsg}</p>
                <div class="progress">
                    <div
                        class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style="width: {progress}%;"
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100">
                        {progress}%
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
#systemLogoImg {
    height: 2rem;
    border-radius: 50%;
}
</style>
